import { morph } from '@theme/morph';

/**
 * Switches between shade products (the colour swatches in the offer card) without a page load.
 * Every shade is its own product, so we fetch the other product page and morph <main> in place,
 * the same way the theme does for combined listings. Hovering/touching a swatch prefetches the page,
 * so the swap is usually instant.
 */

const SWATCH = '.offer-card__swatch';
const CURRENT = 'offer-card__swatch--current';

/** @type {Map<string, Promise<string>>} */
const cache = new Map();
let token = 0;

/** @param {string} url */
function load(url) {
  let request = cache.get(url);

  if (!request) {
    request = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        cache.delete(url);
        throw error;
      });
    cache.set(url, request);
  }

  return request;
}

/** @param {Event} event */
function prefetch(event) {
  const swatch = event.target instanceof Element ? event.target.closest(SWATCH) : null;
  if (swatch instanceof HTMLAnchorElement && !swatch.classList.contains(CURRENT)) {
    load(swatch.href).catch(() => {});
  }
}

document.addEventListener('pointerover', prefetch, { passive: true });
document.addEventListener('touchstart', prefetch, { passive: true });
document.addEventListener('focusin', prefetch);

document.addEventListener('click', async (event) => {
  const swatch = event.target instanceof Element ? event.target.closest(SWATCH) : null;
  if (!(swatch instanceof HTMLAnchorElement)) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  event.preventDefault();
  if (swatch.classList.contains(CURRENT)) return;

  const url = swatch.href;
  const currentToken = ++token;

  // Instant feedback: move the check to the clicked swatch right away.
  const list = swatch.closest('.offer-card__swatches');
  list?.querySelectorAll(`.${CURRENT}`).forEach((element) => {
    element.classList.remove(CURRENT);
    element.removeAttribute('aria-current');
  });
  swatch.classList.add(CURRENT);
  swatch.setAttribute('aria-current', 'true');

  const pickedOffer = document.querySelector('.product-details .offer-card input[type="radio"]:checked');
  const pickedValue = pickedOffer instanceof HTMLInputElement ? pickedOffer.value : null;

  try {
    const html = await load(url);
    if (currentToken !== token) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = document.querySelector('main');
    const newMain = doc.querySelector('main');
    if (!main || !newMain) throw new Error('No main element');

    morph(main, newMain);
    history.replaceState({}, '', url);
    document.title = doc.title;

    // Keep the chosen offer (1 pen / 3+2) when the page content is swapped.
    if (pickedValue) {
      const input = document.querySelector(`.product-details .offer-card input[type="radio"][value="${CSS.escape(pickedValue)}"]`);
      if (input instanceof HTMLInputElement) input.checked = true;
    }

    document.dispatchEvent(new CustomEvent('shade-switcher:updated'));
  } catch {
    // Anything went wrong: fall back to a normal page load.
    if (currentToken === token) window.location.href = url;
  }
});
